import React, {PropsWithChildren} from "react";
import {Button} from "../../../basic-ui-components/button/base/button.component";
import { observer } from "mobx-react";
import { action, observable, makeObservable } from "mobx";
import styled from "styled-components"
import { OrbitComponent } from "../../base-components/orbit-component/orbit-component";
import { IModalDialogHandler, ShowModalOptions } from "../../../services/dialog/dialog.service.interface";
import { Check } from "@sparkware/app-utils";
import { ShowcaseDropdownComponent } from "../showcase-dropdown.component";
import { TextInputBox } from "../../../basic-ui-components/form/textinput.box";
import { TextareaBox } from "../../../basic-ui-components/form/textarea.box";
import {ShowcaseContentContainerBox} from "../boxes/showcase-content-container/showcase-content-container.box";
import {ShowcaseConfigurationBox} from "../boxes/showcase-configuration/showcase-configuration.box";
import {DialogTextBox} from "../../modal-dialog/boxes/text/dialog-text.box";
import {ModalDialogActionsComponent} from "../../modal-dialog/modal-dialog-actions.component";

const Label = styled.label`
    display: block;
    font-size: 1.6rem;
`;

const showEditBox = (options: {
    fieldName: keyof ShowModalOptions;
    caption: string;
    value: string;
    onValueChanged: (fieldName: string, value: string) => void
}) => {
    return <div>
        <Label htmlFor={options.fieldName}>{options.caption}</Label>
        <TextInputBox
            style={{fontSize: "1.6rem", width: "100%"}}
            type="text" name={options.fieldName} id={options.fieldName} value={options.value}
            onChange={(ev) => {
                options.onValueChanged(options.fieldName, ev.target.value);
            }}
        />
    </div>;
}

const showTextAreaBox = (options: {
    fieldName: string;
    caption: string;
    value: string;
    onValueChanged: (fieldName: string, value: string) => void
}) => {
    return <div>
        <Label htmlFor={options.fieldName}>{options.caption}</Label>
        <TextareaBox
            style={{fontSize: "1.6rem", width: "100%"}}
            rows={4}
            name={options.fieldName} id={options.fieldName} value={options.value}
            onChange={(ev) => {
                options.onValueChanged(options.fieldName, ev.target.value);
            }}
        />
    </div>;
}

const showBooleanDropDownBox = (options: {
    fieldName: keyof ShowModalOptions;
    caption: string;
    onValueChanged: (fieldName: string, value: boolean) => void,
    defaultValue?: boolean
}) => {
    return <ShowcaseDropdownComponent
        caption={options.caption}
        fieldName={options.fieldName}
        values={["true", "false"]}
        onChanged={(fieldName, value) => {
            options.onValueChanged(fieldName, value === "" ? undefined : value === "true");
        }}
        defaultValue={options.defaultValue? options.defaultValue.toString() : undefined}
    />
}

@observer
export class PopoupsShowcase extends OrbitComponent {
    @observable.ref
    private _contentText = `Lorem ipsum dolor sit amet,
consectetur adipiscing elit,
sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.
Ut enim ad minim veniam,
quis nostrud exercitation ullamco laboris nisi ut aliquip ex ea commodo consequat.
Duis aute irure dolor in reprehenderit in voluptate velit esse cillum dolore eu fugiat nulla pariatur.
Excepteur sint occaecat cupidatat non proident, sunt in culpa qui officia deserunt mollit anim id est laborum.`;

    @observable.ref
    private _popupOptions: ShowModalOptions = {
        title: "Default title"
    };

    private _dialogHandler: IModalDialogHandler;

    constructor(props: PropsWithChildren) {
        super(props);

        makeObservable(this);
    }

    @action.bound
    _onUpdateFieldNameValue(fieldName: keyof ShowModalOptions, value: any) {
        if (Check.isUndefined(value)) {
            delete this._popupOptions[fieldName];
            return;
        }

        this._popupOptions[fieldName] = value as never;
    }

    @action.bound
    _onUpdateContentValue(fieldName: string, value: any) {
        this._contentText = value;
    }

    render() {
        return <ShowcaseContentContainerBox>
            <ShowcaseConfigurationBox>
                {showEditBox({
                        fieldName: "title",
                        caption: "Title text",
                        value: this._popupOptions.title,
                        onValueChanged: this._onUpdateFieldNameValue
                    }
                )}
                {showTextAreaBox({
                        fieldName: "",
                        caption: "Content text",
                        value: this._contentText,
                        onValueChanged: this._onUpdateContentValue
                    }
                )}

                <Label htmlFor="shopPopupJSON">Resulting popup options:</Label>
                <br/>
                <TextareaBox
                    style={{color: "yellow", width: "100%"}}
                    rows={10}
                    name="shopPopupJSON" id="shopPopupJSON" value={JSON.stringify(this._popupOptions, undefined, 2)}
                    disabled
                />
            </ShowcaseConfigurationBox>

            <ShowcaseConfigurationBox>
                {showBooleanDropDownBox({
                        fieldName: "hideCloseButton",
                        caption: "Hide close button",
                        onValueChanged: this._onUpdateFieldNameValue
                    }
                )}
                {showBooleanDropDownBox({
                        fieldName: "hideBorders",
                        caption: "Hide borders",
                        onValueChanged: this._onUpdateFieldNameValue
                    }
                )}
                {showBooleanDropDownBox({
                        fieldName: "showOpenAndCloseAnimation",
                        caption: "Show hide and close animation",
                        onValueChanged: this._onUpdateFieldNameValue
                    }
                )}
                {showBooleanDropDownBox({
                        fieldName: "showMobileSheetModal",
                        caption: "Show mobile sheet modal",
                        onValueChanged: this._onUpdateFieldNameValue
                    }
                )}
                {showBooleanDropDownBox({
                        fieldName: "forceTransparentBackground",
                        caption: "Force transparent background",
                        onValueChanged: this._onUpdateFieldNameValue
                    }
                )}
                {showBooleanDropDownBox({
                        fieldName: "forceTransparentContentBackground",
                        caption: "Force transparent content background",
                        onValueChanged: this._onUpdateFieldNameValue
                    }
                )}
                {showBooleanDropDownBox({
                        fieldName: "hideDialogHeader",
                        caption: "Hide dialog header",
                        onValueChanged: this._onUpdateFieldNameValue
                    }
                )}
                {showBooleanDropDownBox({
                        fieldName: "forceCentralization",
                        caption: "Force centralization",
                        onValueChanged: this._onUpdateFieldNameValue
                    }
                )}
                {showBooleanDropDownBox({
                        fieldName: "disableFullscreenMobile",
                        caption: "Disable fullscreen on mobile",
                        onValueChanged: this._onUpdateFieldNameValue
                    }
                )}
                {showBooleanDropDownBox({
                        fieldName: "mobileCenterHeader",
                        caption: "Mobile center header",
                        onValueChanged: this._onUpdateFieldNameValue
                    }
                )}
                {showBooleanDropDownBox({
                        fieldName: "isHighPriority",
                        caption: "Is high priority",
                        onValueChanged: this._onUpdateFieldNameValue
                    }
                )}
                {showBooleanDropDownBox({
                        fieldName: "shouldBeClosedOnlyByUser",
                        caption: "Should be closed only by the user",
                        onValueChanged: this._onUpdateFieldNameValue
                    }
                )}
                {showBooleanDropDownBox({
                        fieldName: "forceFullscreenTablet",
                        caption: "Force fullscreen on tablet",
                        onValueChanged: this._onUpdateFieldNameValue
                    }
                )}
            </ShowcaseConfigurationBox>

            <div style={{flexBasis: "100%"}}>
                <Button
                    preset="primary"
                    onClick={() => {
                        const popupBody = <div>
                            <DialogTextBox>
                                {this._contentText}
                            </DialogTextBox>
                            <ModalDialogActionsComponent
                                primaryAction={{
                                    text: "CLOSE ME",
                                    onClick: () => {
                                        //make sure that there is a way for user to close popup
                                        this._dialogHandler?.close();
                                    }
                                }}
                                secondaryAction={{
                                    text: "CLOSE ME",
                                    onClick: () => {
                                        //make sure that there is a way for user to close popup
                                        this._dialogHandler?.close();
                                    }
                                }}
                                linkAction={{
                                    text: "I don't want this dialog",
                                    onClick: () => {
                                        //make sure that there is a way for user to close popup
                                        this._dialogHandler?.close();
                                    }
                                }}
                                margin="3rem auto 0"
                            />
                        </div>;

                        const options: ShowModalOptions = {
                            ...this._popupOptions, onClosed: () => {
                                this._dialogHandler = null;
                            }
                        };

                        this._dialogHandler = this.services.dialog.showModal(() => popupBody, options);
                    }}>
                    SHOW POPUP!
                </Button>
            </div>
        </ShowcaseContentContainerBox>
    }
    }